/**
 * Swaps the default profile picture and header (shown for accounts that did
 * not upload their own) for the ones the admin uploaded for the viewer's
 * theme under Server settings > Themes.
 *
 * The server puts the image URLs in CSS variables of the stylesheet built by
 * SiteThemes, one set per theme and color scheme. Reading them from the
 * computed style means the right image is picked automatically, and a switch
 * between light and dark (e.g. the "auto" scheme following the system) only
 * needs a re-scan.
 */

import { THEME_CHANGED_EVENT } from './theme_refresh';

type Kind = 'avatar' | 'header';

const KINDS: Kind[] = ['avatar', 'header'];

// Paths Mastodon uses when an account has no avatar or header
const BUILT_IN_PATHS: Record<Kind, string> = {
  avatar: '/avatars/original/missing.png',
  header: '/headers/original/missing.png',
};

// Set on images we changed: which default it was, and the src we gave it
const KIND_ATTR = 'data-theme-default';
const ORIGINAL_ATTR = 'data-theme-default-original';
const APPLIED_ATTR = 'data-theme-default-applied';

/** Parses a CSS string value such as `"https://x/a\22 b.png"`. */
export const parseCssString = (raw: string): string | null => {
  const value = raw.trim();

  if (value.length < 2) return null;

  const quote = value[0];
  if ((quote !== '"' && quote !== "'") || !value.endsWith(quote)) return null;

  return value
    .slice(1, -1)
    .replace(/\\([0-9a-fA-F]{1,6}) ?/g, (_match, hex: string) =>
      String.fromCodePoint(parseInt(hex, 16)),
    )
    .replace(/\\(.)/g, '$1');
};

const absolute = (url: string) => {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
};

interface Urls {
  // What the API reports for accounts without an image (built-in or admin's
  // standard light upload)
  defaults: Record<Kind, string | null>;
  // What the current theme wants to show instead
  themed: Record<Kind, string | null>;
}

const readUrls = (): Urls => {
  const style = window.getComputedStyle(document.documentElement);
  const read = (name: string) => {
    const value = parseCssString(style.getPropertyValue(name));
    return value ? absolute(value) : null;
  };

  return {
    defaults: {
      avatar: read('--theme-api-default-avatar'),
      header: read('--theme-api-default-header'),
    },
    themed: {
      avatar: read('--theme-default-avatar'),
      header: read('--theme-default-header'),
    },
  };
};

export const defaultKindOf = (
  src: string,
  defaults: Urls['defaults'],
): Kind | null => {
  if (!src) return null;

  const url = absolute(src);
  let path = url;

  try {
    path = new URL(url).pathname;
  } catch {
    // keep the full string
  }

  return (
    KINDS.find(
      (kind) => path.endsWith(BUILT_IN_PATHS[kind]) || url === defaults[kind],
    ) ?? null
  );
};

let urls: Urls | null = null;

const updateImage = (img: HTMLImageElement) => {
  if (!urls) return;

  const src = img.getAttribute('src') ?? '';
  const applied = img.getAttribute(APPLIED_ATTR);

  // A change we made ourselves
  if (applied !== null && applied === src) {
    const kind = img.getAttribute(KIND_ATTR) as Kind | null;
    const original = img.getAttribute(ORIGINAL_ATTR) ?? src;
    const target = kind ? urls.themed[kind] : null;

    // The theme changed: apply the new image, or put the original back
    if ((target ?? original) !== src) {
      const next = target ?? original;
      img.setAttribute(APPLIED_ATTR, next);
      img.setAttribute('src', next);
    }

    return;
  }

  const kind = defaultKindOf(src, urls.defaults);

  if (!kind) {
    // The app replaced our image with a real one
    img.removeAttribute(KIND_ATTR);
    img.removeAttribute(ORIGINAL_ATTR);
    img.removeAttribute(APPLIED_ATTR);
    return;
  }

  const target = urls.themed[kind];

  img.setAttribute(KIND_ATTR, kind);
  img.setAttribute(ORIGINAL_ATTR, src);

  if (target && absolute(src) !== target) {
    img.setAttribute(APPLIED_ATTR, target);
    img.setAttribute('src', target);
  } else {
    img.setAttribute(APPLIED_ATTR, src);
  }
};

const scan = (root: ParentNode) => {
  if (root instanceof HTMLImageElement) {
    updateImage(root);
    return;
  }

  root.querySelectorAll('img').forEach(updateImage);
};

export function setupThemeDefaults() {
  urls = readUrls();

  // The admin changed a theme and a new stylesheet was swapped in
  window.addEventListener(THEME_CHANGED_EVENT, () => {
    urls = readUrls();
    scan(document);
  });

  const themeAttributes = [
    'data-color-scheme',
    'data-theme-mode',
    'data-contrast',
  ];

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.target === document.documentElement &&
        mutation.attributeName &&
        themeAttributes.includes(mutation.attributeName)
      ) {
        urls = readUrls();
        scan(document);
        continue;
      }

      // Nothing to swap in this theme
      if (!KINDS.some((kind) => urls?.themed[kind])) continue;

      if (mutation.type === 'attributes') {
        if (mutation.target instanceof HTMLImageElement) {
          updateImage(mutation.target);
        }
      } else {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      }
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', ...themeAttributes],
  });

  const scanDocument = () => {
    scan(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanDocument, {
      once: true,
    });
  } else {
    scanDocument();
  }
}
